import React, { useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getCategoryFieldDefs } from '../../utils/attributes';
import { categoryVisualMap } from '../CreateListing/categoryVisualMap';
import { pruneStaleAttrs, buildAttrsStateForFieldResolution } from './attrFilterParams';
import './FilterModal.scss';

const CURRENCIES = [
  { code: '',   symbol: '∗',  label: 'Будь-яка' },
  { code: 'ГРН', symbol: '₴', label: 'ГРН' },
  { code: 'USD', symbol: '$', label: 'USD' },
  { code: 'EUR', symbol: '€', label: 'EUR' },
];

const toNumber = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a), kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a[k] !== b[k]) return false;
  return true;
};

// Portal position
function usePortalPosition(triggerRef, isOpen, { width, gap = 8 }) {
  const [style, setStyle] = useState({ top: 0, left: 0, width: width || 260 });

  useEffect(() => {
    if (!isOpen) return;
    const node = triggerRef.current;
    if (!node) return;

    const update = () => {
      const r = node.getBoundingClientRect();
      const w = width || r.width;
      const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
      const top = Math.round(r.bottom + gap);
      setStyle({ top, left: Math.round(left), width: Math.round(w) });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(node);
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [triggerRef, isOpen, width, gap]);

  return style;
}

// Currency dropdown (portal)
function CurrencyDropdown({ list = CURRENCIES, value, onSelect, buttonLabel = 'Валюта' }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const current = useMemo(() => list.find(x => x.code === value) || list[0], [list, value]);
  const portalStyle = usePortalPosition(wrapRef, open, { width: 260, gap: 8 });

  useEffect(() => {
    const onDocClick = (e) => {
      if (!open) return;
      if (e.target.closest('.currdd__dropdown--portal')) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };

    document.addEventListener('pointerdown', onDocClick, true);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('pointerdown', onDocClick, true);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="currdd" ref={wrapRef}>
      <button
        type="button"
        className={`currdd__trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen(s => !s)}
        aria-haspopup="listbox"
        aria-expanded={open ? 'true' : 'false'}
        title={buttonLabel}
      >
        <span className="currdd__selected">
          <span className="currdd__symbol" aria-hidden="true">{current.symbol}</span>
          <span className="currdd__name">{current.label}</span>
        </span>
        <span className="currdd__caret">▾</span>
      </button>

      {open && createPortal(
        <div
          className="currdd__dropdown currdd__dropdown--portal"
          role="listbox"
          style={{ top: portalStyle.top, left: portalStyle.left, width: portalStyle.width }}
        >
          {list.map(item => (
            <button
              key={item.code || 'any'}
              type="button"
              className={`currdd__item ${item.code === value ? 'is-selected' : ''}`}
              role="option"
              aria-selected={item.code === value ? 'true' : 'false'}
              onMouseDown={(e) => { e.preventDefault(); onSelect(item.code); setOpen(false); }}
              title={item.label}
            >
              <span className="currdd__symbol" aria-hidden="true">{item.symbol}</span>
              <span className="currdd__label">{item.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

const FilterModal = ({
  isOpen,
  category,
  initialFilters,
  initialGeneral,
  onApply,
  onReset,
  onClose,
}) => {
  const [visible, setVisible] = useState(isOpen);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [visible]);

  useEffect(() => {
    if (isOpen) { setVisible(true); setClosing(false); }
    else {
      setClosing(true);
      const t = setTimeout(() => { setClosing(false); setVisible(false); }, 220);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  const [localGeneral, setLocalGeneral] = useState({
    category: category || '',
    city: initialGeneral?.city || '',
    currency: initialGeneral?.currency || '',
    priceMin: initialGeneral?.priceMin ?? '',
    priceMax: initialGeneral?.priceMax ?? '',
    sort: initialGeneral?.sort || '',

    free: !!initialGeneral?.free,
    exchange: !!initialGeneral?.exchange,
    wanted: !!initialGeneral?.wanted,

    condition: initialGeneral?.condition || '',

    negotiable: initialGeneral?.negotiable || '',
  });

  const generalSig = useMemo(
    () => JSON.stringify({
      category: category || '',
      city: initialGeneral?.city || '',
      currency: initialGeneral?.currency || '',
      priceMin: initialGeneral?.priceMin ?? '',
      priceMax: initialGeneral?.priceMax ?? '',
      sort: initialGeneral?.sort || '',
      free: !!initialGeneral?.free,
      exchange: !!initialGeneral?.exchange,
      wanted: !!initialGeneral?.wanted,
      condition: initialGeneral?.condition || '',
      negotiable: initialGeneral?.negotiable || '',
    }),
    [category, initialGeneral]
  );

  useEffect(() => {
    const next = JSON.parse(generalSig);
    setLocalGeneral((prev) => (shallowEqual(prev, next) ? prev : next));
  }, [generalSig]);

  const [localAttrs, setLocalAttrs] = useState({});
  const initialFiltersSig = useMemo(() => JSON.stringify(initialFilters || {}), [initialFilters]);

  // Re-hydrate the draft from the parent's real applied state whenever it
  // genuinely changes (initial open, URL navigation, or after a previous
  // Apply) — not on every keystroke inside the modal, and not reactively to
  // `fields` (which itself now depends on localAttrs — see below — so tying
  // this hydration to fields would wipe whatever the user just picked as
  // soon as a deeper field became visible).
  useEffect(() => {
    setLocalAttrs(JSON.parse(initialFiltersSig || '{}'));
  }, [category, initialFiltersSig]);

  // The field list is derived live from whatever the user has picked so
  // far (department -> type -> shoeSize, vehicleType -> brand -> model,
  // ...), reusing the exact same conditional (showIf) resolution Create
  // Listing uses — this is what makes deeper fields progressively appear.
  const attrsForFieldResolution = useMemo(
    () => buildAttrsStateForFieldResolution(localAttrs),
    [localAttrs]
  );

  const fields = useMemo(() => {
    if (!localGeneral.category) return [];
    const arr = getCategoryFieldDefs(localGeneral.category, attrsForFieldResolution) || [];
    return Array.isArray(arr) ? arr : [];
  }, [localGeneral.category, attrsForFieldResolution]);

  const fieldsSig = useMemo(
    () => (fields || [])
      .map((f) => `${f.name}:${f.type}:${Array.isArray(f.options) ? f.options.length : 0}`)
      .join('|'),
    [fields]
  );

  // Once a gating value changes and the field list shifts, drop any
  // selections that no longer belong (a field disappeared, or a
  // select/multiselect value fell outside the now-current option list).
  // This only ever removes stale entries — it never resets a value that's
  // still valid, so live typing/picking is never clobbered. Depends on
  // fieldsSig (fields' stable content signature) rather than `fields`
  // itself on purpose — `fields` is a new array reference every render.
  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => {
    setLocalAttrs((prev) => pruneStaleAttrs(fields, prev));
  }, [fieldsSig]); // eslint-disable-line react-hooks/exhaustive-deps

  const closeWithFade = () => {
    setClosing(true);
    setTimeout(() => { setClosing(false); setVisible(false); onClose?.(); }, 220);
  };

  // Category dropdown (portal)
  const categories = Object.keys(categoryVisualMap || {});
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const catRef = useRef(null);
  const catPortalStyle = usePortalPosition(catRef, showCatDropdown, { gap: 6 });

  // Switching category clears the previous category's detailed filters
  // outright, rather than waiting on the fieldsSig prune effect — a field
  // *name* shared between two categories (e.g. "color") would otherwise
  // survive the prune even though it means something different here.
  const selectCategory = (name) => {
    setLocalGeneral((p) => ({ ...p, category: name }));
    setLocalAttrs({});
    setShowCatDropdown(false);
  };

  useEffect(() => {
    const handleOutside = (e) => {
      if (!showCatDropdown) return;
      if (e.target.closest('.catdd__dropdown--portal')) return;
      if (catRef.current && !catRef.current.contains(e.target)) setShowCatDropdown(false);
    };
    const handleEsc = (e) => { if (e.key === 'Escape') setShowCatDropdown(false); };
    document.addEventListener('pointerdown', handleOutside, true);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('pointerdown', handleOutside, true);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [showCatDropdown]);

  if (!visible) return <div style={{ display: 'none' }} aria-hidden="true" />;

  return (
    <div className={`filters-modal ${closing ? 'is-closing' : 'is-open'}`} onClick={closeWithFade}>
      <div className="filters-modal__content" onClick={(e) => e.stopPropagation()}>
        <div className="filters-modal__header">
          <h3>Фільтри</h3>
          <button className="close-btn" onClick={closeWithFade} aria-label="Закрити">✕</button>
        </div>

        <div className="filters-modal__body">
          {/* */ }
          <div className="filters-section stagger-in">
            <div className="filters-section__title">Загальні</div>

            <div className="filters-row">
              <label>Категорія</label>

              {/* */ }
              <div className="custom-select-wrapper" ref={catRef}>
                <div
                  className={`custom-select-trigger pill-50 ${showCatDropdown ? 'open' : ''}`}
                  onClick={() => setShowCatDropdown(s => !s)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setShowCatDropdown(s => !s)}
                  aria-haspopup="listbox"
                  aria-expanded={showCatDropdown}
                >
                  {localGeneral.category ? (
                    <div className="selected-category">
                      <div
                        className="category-icon"
                        style={{ backgroundImage: `url(${categoryVisualMap[localGeneral.category]})` }}
                      />
                      <span>{localGeneral.category}</span>
                    </div>
                  ) : (
                    <span className="placeholder">Оберіть категорію</span>
                  )}
                  <span className="chevron" aria-hidden="true">▾</span>
                </div>
              </div>

              {showCatDropdown && createPortal(
                <div
                  className="custom-select-dropdown catdd__dropdown--portal open"
                  role="listbox"
                  style={{ top: catPortalStyle.top, left: catPortalStyle.left, width: catPortalStyle.width }}
                >
                  {categories.map((name, idx) => (
                    <div
                      key={name}
                      className="dropdown-item"
                      style={{ ['--item-index']: idx }}
                      onClick={() => selectCategory(name)}
                      role="option"
                      aria-selected={localGeneral.category === name}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          selectCategory(name);
                        }
                      }}
                    >
                      <div
                        className="category-icon"
                        style={{ backgroundImage: `url(${categoryVisualMap[name]})` }}
                      />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>,
                document.body
              )}
            </div>

            <div className="filters-grid-2">
              <div className="filters-row">
                <label>Місто / Локація</label>
                <input
                  type="text"
                  placeholder="Напр. Київ"
                  value={localGeneral.city}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLocalGeneral((p) => ({ ...p, city: v }));
                  }}
                />
              </div>

              <div className="filters-row">
                <label>Валюта</label>
                <CurrencyDropdown
                  value={localGeneral.currency}
                  onSelect={(code) => setLocalGeneral((p) => ({ ...p, currency: code }))}
                />
              </div>
            </div>

            <div className="filters-row">
              <label>Ціна</label>
              <div className="filters-number-range">
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="від"
                  value={localGeneral.priceMin}
                  onChange={(e) => setLocalGeneral((p) => ({ ...p, priceMin: e.target.value }))}
                />
                <span>–</span>
                <input
                  type="number"
                  inputMode="numeric"
                  placeholder="до"
                  value={localGeneral.priceMax}
                  onChange={(e) => setLocalGeneral((p) => ({ ...p, priceMax: e.target.value }))}
                />
              </div>
            </div>

            <div className="filters-chips">
              <button
                className={`chip ${localGeneral.free ? 'active' : ''}`}
                onClick={() => setLocalGeneral((p) => ({ ...p, free: !p.free }))}
                type="button"
              >
                Безкоштовно
              </button>

              <button
                className={`chip ${localGeneral.exchange ? 'active' : ''}`}
                onClick={() => setLocalGeneral((p) => ({ ...p, exchange: !p.exchange }))}
                type="button"
              >
                Обмін
              </button>

              <button
                className={`chip ${localGeneral.wanted ? 'active' : ''}`}
                onClick={() => setLocalGeneral((p) => ({ ...p, wanted: !p.wanted }))}
                type="button"
              >
                Шукаю
              </button>
            </div>

            <div className="filters-row">
              <label>Стан</label>
              <div className="sort-pills">
                <button
                  type="button"
                  className={localGeneral.condition === 'new' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, condition: p.condition === 'new' ? '' : 'new' }))}
                >
                  Нове
                </button>

                <button
                  type="button"
                  className={localGeneral.condition === 'used' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, condition: p.condition === 'used' ? '' : 'used' }))}
                >
                  Вживане
                </button>

                <button
                  type="button"
                  className={localGeneral.condition === 'na' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, condition: p.condition === 'na' ? '' : 'na' }))}
                  title="Коли «стан» не має сенсу (послуги, тварини тощо)"
                >
                  Інше
                </button>
              </div>
            </div>

            {}
            <div className="filters-row">
              <label>Торг</label>
              <div className="sort-pills">
                <button
                  type="button"
                  className={localGeneral.negotiable === 'yes' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, negotiable: p.negotiable === 'yes' ? '' : 'yes' }))}
                  title="Лише оголошення з увімкненим торгом"
                >
                  Можливий торг
                </button>
              </div>
            </div>

            <div className="filters-row">
              <label>Сортування</label>
              <div className="sort-pills">
                <button
                  type="button"
                  className={localGeneral.sort === 'expensive' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, sort: p.sort === 'expensive' ? '' : 'expensive' }))}
                >
                  Спочатку дорожчі
                </button>

                <button
                  type="button"
                  className={localGeneral.sort === 'cheap' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, sort: p.sort === 'cheap' ? '' : 'cheap' }))}
                >
                  Спочатку дешевші
                </button>

                <button
                  type="button"
                  className={localGeneral.sort === 'new' ? 'active' : ''}
                  onClick={() => setLocalGeneral((p) => ({ ...p, sort: p.sort === 'new' ? '' : 'new' }))}
                >
                  Найсвіжіші за датою
                </button>
              </div>
            </div>
          </div>

          {/* */ }
          <div className={`filters-section cat-fields ${localGeneral.category ? 'enter' : 'leave'}`}>
            <div className="filters-section__title">За категорією</div>

            {!localGeneral.category && <div className="filters-hint">Оберіть категорію, щоб побачити поля.</div>}

            {localGeneral.category && (fields || []).map((f, idx) => {
              const rowClass = `filters-row fade-in`;
              if (f.type === 'number') {
                return (
                  <div className={rowClass} style={{ ['--i']: idx }} key={f.name}>
                    <label>{f.label}</label>
                    <div className="filters-number-range">
                      <input
                        type="number"
                        inputMode="numeric"
                        placeholder="від"
                        value={localAttrs[f.name]?.min ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setLocalAttrs((prev) => ({ ...prev, [f.name]: { ...(prev[f.name] || { type: 'number' }), min: val } }));
                        }}
                      />
                      <span>–</span>
                      <input
                        type="number"
                        inputMode="numeric"
                        placeholder="до"
                        value={localAttrs[f.name]?.max ?? ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setLocalAttrs((prev) => ({ ...prev, [f.name]: { ...(prev[f.name] || { type: 'number' }), max: val } }));
                        }}
                      />
                    </div>
                  </div>
                );
              }

              if (f.type === 'select') {
                return (
                  <div className={rowClass} style={{ ['--i']: idx }} key={f.name}>
                    <label>{f.label}</label>
                    <select
                      value={localAttrs[f.name]?.value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setLocalAttrs((prev) => ({ ...prev, [f.name]: { ...(prev[f.name] || { type: 'text' }), value: val } }));
                      }}
                    >
                      <option value="">Неважливо</option>
                      {(f.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </div>
                );
              }

              if (f.type === 'multiselect') {
                const selected = Array.isArray(localAttrs[f.name]?.value) ? localAttrs[f.name].value : [];

                const toggleOption = (opt) => {
                  setLocalAttrs((prev) => {
                    const current = Array.isArray(prev[f.name]?.value) ? prev[f.name].value : [];
                    const next = current.includes(opt)
                      ? current.filter((v) => v !== opt)
                      : [...current, opt];
                    return { ...prev, [f.name]: { type: 'text', value: next } };
                  });
                };

                return (
                  <div className={rowClass} style={{ '--i': idx }} key={f.name}>
                    <label>{f.label}</label>
                    <div className="filters-chips">
                      {(f.options || []).map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          className={`chip ${selected.includes(opt) ? 'active' : ''}`}
                          onClick={() => toggleOption(opt)}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <div className={rowClass} style={{ ['--i']: idx }} key={f.name}>
                  <label>{f.label}</label>
                  <input
                    type="text"
                    placeholder="Введіть значення"
                    value={localAttrs[f.name]?.value ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setLocalAttrs((prev) => ({ ...prev, [f.name]: { ...(prev[f.name] || { type: 'text' }), value: val } }));
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <div className="filters-modal__note">
          Ми постійно вдосконалюємо фільтрування, щоб пошук був ще точнішим, швидшим і зручнішим для вас✨
        </div>

        <div className="filters-modal__footer">
          <button
            className="fm-btn ghost"
            type="button"
            onClick={() => {
              setLocalAttrs({});
              setLocalGeneral({
                category: '',
                city: '',
                currency: '',
                priceMin: '',
                priceMax: '',
                sort: '',
                free: false,
                exchange: false,
                wanted: false,
                condition: '',
                negotiable: '',
              });
              onReset?.();
            }}
          >
            Скинути фільтри
          </button>

          <button
            className="fm-btn primary"
            type="button"
            onClick={() => {
              const preparedGeneral = {
                category: localGeneral.category || '',
                city: (localGeneral.city || '').trim(),
                currency: localGeneral.currency || '',
                priceMin: toNumber(localGeneral.priceMin),
                priceMax: toNumber(localGeneral.priceMax),

                sort: localGeneral.sort || '',

                free: !!localGeneral.free,
                exchange: !!localGeneral.exchange,
                wanted: !!localGeneral.wanted,
                condition: localGeneral.condition || '',

                negotiable: localGeneral.negotiable || '',
              };

              let preparedAttrs = {};
              if (localGeneral.category) {
                Object.entries(localAttrs).forEach(([name, f]) => {
                  if (f?.type === 'number') {
                    const min = toNumber(f.min), max = toNumber(f.max);
                    if (min !== null || max !== null) preparedAttrs[name] = { type: 'number', min, max };
                  } else if (Array.isArray(f?.value)) {
                    if (f.value.length > 0) preparedAttrs[name] = { type: 'text', value: f.value };
                  } else if ((f?.value ?? '') !== '') {
                    preparedAttrs[name] = { type: 'text', value: f.value };
                  }
                });
              }

              onApply(preparedAttrs, preparedGeneral);
              closeWithFade();
            }}
          >
            Застосувати
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
